
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './PhoneNumberAndIncome.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { getAppData } from '../../utils/acquisition-utils';
import SessionStorage from '../../utils/SessionStorage';
import useBriteVerify from '../../hooks/useBriteVerify';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import MonthlyIncomeField from '../FieldContainers/MonthlyIncomeField/MonthlyIncomeField';
import { cleanNumericInput } from '../../utils/income-utils';
import { Row, Col, Title, Section } from '../../importCL';
import PhoneNumberField from '../FieldContainers/PhoneNumberField/PhoneNumberField';
import PhoneNumberFieldWithProvider from '../FieldContainers/PhoneNumberField/PhoneNumberFieldWithProvider';
import { useFeatureFlags } from '../../providers/FeatureFlagProvider/FeatureFlagProvider';
import useAcquisitionProviderFacade from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import { TErrorType } from '../../providers/ModernAcquisitionProvider/types';
import { IPersonalInfo } from '../../providers/AcquisitionProvider/models';

interface IPhoneNumberAndIncomeProps extends MappedComponentProperties {
    subtitle?: string;
    phoneLabel?: string;
    incomeLabel?: string;
    incomeTooltip?: string;
    title?: string;
    version?: string;
    phoneDisclaimer?: string;
    incomeDisclaimer?: string;
    incomeDescription?: string;
    topBorderEnabled?: boolean;
    bottomBorderEnabled?: boolean;
    popUp?: boolean;
    popUpHeadline?: string;
    popUpSubHeadline?: string;
    popUpCtaKeep?: string;
    popUpCtaDismiss?: string;
}

export const PhoneNumberAndIncomeEditConfig = {
    emptyLabel: 'Phone Number And Income',
    isEmpty: (props: PropsWithChildren<IPhoneNumberAndIncomeProps>) => {
        return !props || !props.phoneLabel;
    },
};

const PhoneNumberAndIncome = (props: PropsWithChildren<IPhoneNumberAndIncomeProps>): JSX.Element => {

    const [phoneNumber, setPhoneNumber] = useState('');
    const [monthlyIncome, setMonthlyIncome] = useState('');
    const { setIsMonthlyIncomeValid, isMonthlyIncomeValid } = useAcquisitionContext();
    const [plist] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:plist')?.split(',').map(phone => atob(phone)) ?? []);

    const phoneEl = useRef<HTMLInputElement>(null);
    const monthlyIncomeRef = useRef<HTMLInputElement>(null);
    const [lastPhone, setLastPhone] = useState<{ value: string, result: boolean, validationResult?: any }>({ value: '', result: false, validationResult: {} });
    const { phoneValidationApi } = useBriteVerify();
    const { getFeatureFlags } = useFeatureFlags();
    const { getIsPhoneNumberValid, personalInformationWrite, getPersonalInformation } = useAcquisitionProviderFacade();

    const handlePhoneBlur = async () => {
        const phone = phoneNumber.replace(/[^0-9]/g, '');

        if (!phone || phone.length !== 10) {
            personalInformationWrite({ phoneNumber: { value: phone, state: 'error', errorType: 'requiredField' } })
            return;
        }
        if (plist.length > 0) {
            if (plist.includes(phone)) {
                personalInformationWrite({ phoneNumber: { value: phone, state: 'error', errorType: 'invalidInput' } })
                return;
            }
        }
        try {
            const isAuthor = (window.location.host.indexOf('4502') > -1) || (window.location.host.indexOf('3000') > -1);
            let isValid = true;
            if ((!SessionStorage.contains('adobeMode') && !isAuthor) || (SessionStorage.contains('adobeMode') && SessionStorage.contains('usePhoneApi'))) {
                if (phone === lastPhone.value) {
                    isValid = lastPhone.result;
                    if (lastPhone?.validationResult) {
                        SessionStorage.set('bvrslt', btoa(JSON.stringify(lastPhone.validationResult)));
                    }
                } else {
                    const validationResult = await phoneValidationApi(phone);
                    isValid = !!validationResult;
                    setLastPhone({ value: phone, result: isValid, validationResult });
                    if (validationResult && validationResult !== 'skipped') {
                        SessionStorage.set('bvrslt', btoa(JSON.stringify(validationResult)));
                    }
                }
            }
            personalInformationWrite({ phoneNumber: { value: phone, state: isValid ? 'success' : 'error', errorType: isValid ? 'None' : 'thirdParty' } })

            if (isValid) {
                SessionStorage.set('phoneNumber', phone);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const phone = getPersonalInformation().phoneNumber?.value?.replace(/[^0-9]/g, '');
        const errorListener = () => {
            if (!phone && getIsPhoneNumberValid()) {
                personalInformationWrite({ phoneNumber: { state: 'error', errorType: 'requiredField' } });
            }
        };
        window.addEventListener('acquisitionErrorToggle', errorListener);
        return () => window.removeEventListener('acquisitionErrorToggle', errorListener);
    }, [getIsPhoneNumberValid]);

    useEffect(() => {
        getAppData().then(appData => {
            if (appData.PersonalInformation) {
                const personalInfo = appData.PersonalInformation;
                setPhoneNumber(personalInfo.PrimaryPhoneNumber);
                setMonthlyIncome(personalInfo.MonthlyIncome);
                personalInformationWrite({
                    phoneNumber: { value: personalInfo.PrimaryPhoneNumber, state: 'neutral', errorType: 'None' },
                    monthlyIncome: { value: personalInfo.MonthlyIncome, state: 'neutral', errorType: 'None' }
                });
            }
        });
    }, []);
    
    const handleMonthlyIncomeChange = (value: string) => {
        setMonthlyIncome(value);
        const monthlyIncomeErrorType: TErrorType = !value
            ? 'requiredField'
            : cleanNumericInput(value).length < 3
                ? 'invalidInput'
                : 'None'
        personalInformationWrite({
            monthlyIncome: { value: value, state: monthlyIncomeErrorType === 'None' ? 'success' : 'error', errorType: monthlyIncomeErrorType }
        });
    };

    return (
        <div id='phone-number-and-income' className='PhoneNumberAndIncome'>
            <Section className='PhoneNumberAndIncome__section'>
                {props.topBorderEnabled && <hr role='separator' />}
                <Row className='px-4'>
                    <Col>
                        <Title tag='h3' className='PhoneNumberAndIncome__title'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}</Title>
                        <div className='PhoneNumberAndIncome__note'>{transformHTMLParserLinks(props.subtitle ?? '')}</div>
                    </Col>
                </Row>
                <Row className='px-4'>
                    <Col md={4} id='phone-field-container'>
                        {getFeatureFlags('DPM-14217') ? (
                            <PhoneNumberFieldWithProvider
                                fieldKey='phoneNumber'
                                name='Phone Number'
                                label={wrapTrademarkSuperscript(props.phoneLabel)}
                            />
                        ) : (
                            <PhoneNumberField
                                ref={phoneEl}
                                label={transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneLabel))}
                                value={phoneNumber}
                                setValueCallback={setPhoneNumber}
                                error={!getIsPhoneNumberValid()}
                                onBlurCallback={handlePhoneBlur}
                                required
                                alternate
                                useSuccessIcon
                                disclaimerMessage={transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneDisclaimer))}
                                disclaimerClassName='PhoneNumberAndIncome__slideDown PhoneNumberAndIncome__disclaimerText'
                            />
                        )}
                    </Col>
                    <Col md={4} id='monthlyIncome-field-container'>
                        <MonthlyIncomeField
                            ref={monthlyIncomeRef}
                            monthlyIncomeLabel={props.incomeLabel}
                            incomeTooltip={props.incomeTooltip}
                            handleSetValue={handleMonthlyIncomeChange}
                            handleIsValidCallback={setIsMonthlyIncomeValid}                            
                            monthlyIncome={monthlyIncome}
                            incomeMessage={props.incomeDisclaimer}
                            incomeDescription={props.incomeDescription}
                            popUp={props.popUp}
                            popUpHeadline={props.popUpHeadline}
                            popUpSubHeadline={props.popUpSubHeadline}
                            popUpCtaDismiss={props.popUpCtaDismiss}
                            popUpCtaKeep={props.popUpCtaKeep}
                        />
                    </Col>
                </Row>
                {props.bottomBorderEnabled && <hr role='separator' />}
            </Section>
        </div>
    );
};

export default PhoneNumberAndIncome;